import { useCallback, useEffect, useRef, useState } from 'react';

// A lazily-loaded AWS Organizations tree: one level per request, cached for the
// lifetime of the view. Modelled on the Accounts hierarchy view
// (pages/ManualOnboarding.js), extracted so the StackSets pickers share it.
//
// Expansion is controlled here rather than in the row component, because the
// levels live in this cache and the counting walk (utils/orgCount.js) reads and
// writes the same cache object directly, bypassing React state.
//
// Error-handling contract: `loadChildren` re-throws on failure so the counting
// walk (which calls it as `getChildren`) can fail loudly and stop the walk.
// `toggle` and `retry` deliberately swallow that same error, because by the
// time it reaches them it is already recorded in `errors[nodeId]` for the row
// to render — there's nothing left for the caller to do with it.
//
// fetchChildren(parentId) resolves prism-ops' {parent?, children} shape. The
// endpoint returns the tenant root as `parent` only when parentId is omitted,
// which is the initial load.

// The backend writes prism-ops' raw body through; older portal endpoints wrap
// payloads in {success, data}. Accept either shape, but only these two — an
// unrecognised response fails loudly instead of silently degrading into an
// empty tree. Module-scoped (captures nothing) so it isn't an exhaustive-deps
// foot-gun for the callbacks below. Uses `in` rather than truthiness so a
// legitimately empty or null level ({children: []} / {children: null}) is
// still recognised rather than mistaken for an unrecognised shape.
const unwrap = (res) => {
  if (res && ('children' in res || 'parent' in res)) return res;
  if (res && res.data && ('children' in res.data || 'parent' in res.data)) return res.data;
  throw new Error('unrecognised organization hierarchy response');
};

export function useLazyOrgTree(fetchChildren) {
  const [root, setRoot] = useState(null);
  const [children, setChildren] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [topLoading, setTopLoading] = useState(false);
  const [topError, setTopError] = useState('');

  // Mirrors `children` as a mutable object so countAccountsUnderOUs can read and
  // write the same cache without going through React state on every level.
  const cacheRef = useRef({});
  const topLoadingRef = useRef(false);

  // One promise per node currently in flight, so a double-clicked chevron, two
  // quick "Try again" clicks, or a manual expand racing the counting walk share
  // a single AWS Organizations call for that node instead of firing a second —
  // exactly the throughput this redesign exists to protect.
  const inFlightRef = useRef({});

  // Bumped by refresh() so a fetch already in flight when the user refreshes
  // cannot write pre-refresh data into the post-refresh cache/state: every
  // commit below is guarded by comparing against the epoch captured when that
  // fetch started.
  const epochRef = useRef(0);

  // Held in a ref so the hook cannot be broken by a caller passing an inline
  // arrow: depending on fetchChildren's identity would give loadTop a new
  // identity every render and make the mount effect refetch forever.
  const fetchRef = useRef(fetchChildren);
  useEffect(() => { fetchRef.current = fetchChildren; }, [fetchChildren]);

  const loadChildren = useCallback(async (nodeId) => {
    const cached = cacheRef.current[nodeId];
    if (cached) {
      // Re-publish: the counting walk can fill the cache without going through
      // React state, and a row whose level is cached but absent from
      // `children` would render its loading fallback forever.
      setChildren((prev) => (prev[nodeId] === cached ? prev : { ...prev, [nodeId]: cached }));
      return cached;
    }

    if (inFlightRef.current[nodeId]) return inFlightRef.current[nodeId];

    const epoch = epochRef.current;
    const p = (async () => {
      setLoading((prev) => ({ ...prev, [nodeId]: true }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next[nodeId];
        return next;
      });
      try {
        const payload = unwrap(await fetchRef.current(nodeId));
        const level = payload.children || [];
        if (epochRef.current !== epoch) return level; // superseded by a refresh
        cacheRef.current[nodeId] = level;
        setChildren((prev) => ({ ...prev, [nodeId]: level }));
        return level;
      } catch (err) {
        if (epochRef.current === epoch) {
          setErrors((prev) => ({ ...prev, [nodeId]: err.message || 'Failed to load this level.' }));
        }
        throw err;
      } finally {
        if (epochRef.current === epoch) {
          setLoading((prev) => {
            const next = { ...prev };
            delete next[nodeId];
            return next;
          });
        }
        // Only clear our own entry — a stale promise settling after a refresh
        // must not delete a newer fetch's in-flight marker for the same node.
        if (inFlightRef.current[nodeId] === p) delete inFlightRef.current[nodeId];
      }
    })();

    inFlightRef.current[nodeId] = p;
    return p;
  }, []);

  // Initial load: the root row plus its immediate children. prism-ops returns
  // the root itself as `parent` only when parentId is absent, which is this
  // call. A response with no `parent` is treated as a failure rather than a
  // fabricated empty tenant — a synthetic id is not a real OU, so selecting it
  // as a target would send a placeholder to the backend, and expanding it would
  // request children for a node that doesn't exist.
  const loadTop = useCallback(async () => {
    if (topLoadingRef.current) return;
    topLoadingRef.current = true;
    setTopLoading(true);
    setTopError('');
    const epoch = epochRef.current;
    try {
      const payload = unwrap(await fetchRef.current(undefined));
      if (!payload.parent) {
        throw new Error('the organization hierarchy response did not include a root node');
      }
      if (epochRef.current !== epoch) return; // superseded by a refresh
      const rootNode = payload.parent;
      const level = payload.children || [];
      cacheRef.current = { [rootNode.id]: level };
      setRoot(rootNode);
      setChildren({ [rootNode.id]: level });
      setExpanded({ [rootNode.id]: true });
      setErrors({});
      setLoading({});
    } catch (err) {
      if (epochRef.current === epoch) {
        setRoot(null);
        setTopError(err.message || 'Failed to load the organization hierarchy.');
      }
    } finally {
      topLoadingRef.current = false;
      setTopLoading(false);
    }
  }, []);

  useEffect(() => { loadTop(); }, [loadTop]);

  // toggle only flips `expanded` and, on first expansion, kicks off the fetch —
  // it never derives the "did this just become expanded" decision from inside a
  // setState updater. Updaters can run more than once (StrictMode double-invoke,
  // batched re-renders), and firing a network call as a side effect of one would
  // risk a duplicate request; reading `expanded` directly keeps that decision a
  // single, plain read. It always routes through loadChildren — which itself
  // decides cache vs. in-flight vs. network — rather than checking the cache
  // here too, so there is exactly one place that answers "do we already have
  // this level" and the dual-cache divergence that check used to allow can't
  // recur.
  const toggle = useCallback((node) => {
    const nodeId = node.id;
    const willExpand = !expanded[nodeId];
    setExpanded((prev) => ({ ...prev, [nodeId]: willExpand }));
    if (willExpand) {
      loadChildren(nodeId).catch(() => {}); // the error lands on the row
    }
  }, [expanded, loadChildren]);

  const retry = useCallback(async (nodeId) => {
    delete cacheRef.current[nodeId];
    try {
      await loadChildren(nodeId);
    } catch (_) { /* recorded on the row */ }
  }, [loadChildren]);

  const refresh = useCallback(() => {
    epochRef.current += 1;
    inFlightRef.current = {};
    cacheRef.current = {};
    setChildren({});
    setExpanded({});
    setErrors({});
    setLoading({});
    setRoot(null);
    loadTop();
  }, [loadTop]);

  return {
    root, children, expanded, loading, errors, topLoading, topError,
    toggle, retry, refresh, loadChildren, cacheRef,
  };
}

export default useLazyOrgTree;
